<template>
  <v-layout
    id="summary"
    column
    :class="isPreferencesSummary ? 'settings-page align-center' : 'pa-1'"
  >
    <template v-if="isPreferencesSummary">
      <v-card id="preferences" class="settings-card mx-auto mb-5" flat>
        <v-card-title class="settings-card__header">
          {{ $t('app.settings.title') }} &rsaquo; {{ $t('app.summary.user.title') }}
        </v-card-title>
        <div class="settings-card__body assessment-light panel-opaque">
          <div v-for="(panel, index) in preferenceItems" :key="panel.id">
            <list class="assessment-light panel-opaque preference-item" v-bind:item="panel"></list>
            <v-divider
              v-if="index < preferenceItems.length - 1"
              class="settings-divider"
            ></v-divider>
          </div>
        </div>
      </v-card>

      <v-card
        id="user-interface"
        v-if="isFirstLaunchReview"
        class="settings-card mx-auto mb-5"
        flat
      >
        <v-card-title class="settings-card__header">
          <mrx-translation token="app.settings.user_interface.title"></mrx-translation>
        </v-card-title>
        <div class="settings-card__body assessment-light panel-opaque">
          <user-interface-preferences></user-interface-preferences>
        </div>
      </v-card>
    </template>

    <template v-else>
      <v-card
        v-for="(collection, type) in collections"
        :key="type"
        class="mb-4 mx-5 intro-dark"
        flat
      >
        <v-card-title class="font-weight-bold justify-center" :id="`${type}-header`">
          <mrx-translation :token="`app.summary.${type}.title`"></mrx-translation>
        </v-card-title>
        <v-card-text class="assessment-light panel-opaque pt-4">
          <div v-for="(panel, index) in collection" :key=index>
            <list class="assessment-light panel-opaque" v-bind:item="panel" :key="panel.id" :ref="`${type}List${index}`">
            </list>
            <v-divider v-if="index < collection.length - 1" :key="`${panel.id}-divider`"></v-divider>
          </div>
        </v-card-text>
      </v-card>

      <div
        v-if="allowCopyPaste"
        class="d-flex flex-column align-center pt-2 pb-2 mx-5"
        style="width: 90%;"
      >
        <CopyClipboardButton
          token="app.assessment.summary.button.copy.title"
          name="summary"
          cssClass="copy-all-btn"
          :payload="allSummaryPayload"
          mode="button"
          style="width: 100%;"
        />
      </div>
    </template>
  </v-layout>
</template>

<script>
import Bottom from '@/components/layouts/Bottom'
import List from '@/components/layouts/List'
import CopyClipboardButton from '@/components/widgets/CopyClipboardButton'
import UserInterfacePreferences from '@/components/widgets/UserInterfacePreferences'
import groupBy from 'lodash.groupby'
import ui from '@/mixins/ui'
import { addLocationCollection } from '@/utils/location'
import { SummaryPayloadBuilder } from '@/utils/summaryPayloadUtils'
import { mapState } from 'pinia'
import { useLanguageStore } from '@/stores/language';
import { useTestModeStore } from '@/stores/testMode';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useLocationStore } from '@/stores/location';
import { useLaunchSetupStore } from '@/stores/LaunchSetup'
import './styles/settings.css'
import { usePreferenceStore } from '@/stores/preference';
import { resetEncounter } from '@/utils/encounterLifecycle';

export default {
  mixins: [ui],
  components: { Bottom, List, CopyClipboardButton, UserInterfacePreferences },
  name: 'Summary',
  
  data() {
    return {
      collections: null,
      title: null,
      nextLink: null,
      allCopyPayloads: {},
    }
  },

  props: {
    maxHeightOffset: {
      type: Number,
      default: 0
    },
    initialMaxHeightOffset: {
      type: Number,
      default: 0
    }
  },

  computed: {
    isPreferencesSummary() {
      return this.$route.meta.collection === 'preference';
    },

    isSetupSummary() {
      return this.isPreferencesSummary && this.$route.query.setup === 'true';
    },

    isFirstLaunchReview() {
      return this.isSetupSummary && !this.isSetupComplete;
    },

    preferenceItems() {
      return this.collections ? Object.values(this.collections).flat() : [];
    },

    allSummaryPayload() {
      const builder = new SummaryPayloadBuilder(this, this.rtl);
      return builder.buildCombinedPayload(this.allCopyPayloads);
    },

    ...mapState(useLanguageStore, { rtl: 'rtl', language: 'selected' }),
    ...mapState(useTestModeStore, { getMode: 'get' }),
    ...mapState(usePartnerConfigStore, ['allowCopyPaste']),
    ...mapState(useLocationStore, ['siteSummary', 'siteLocationSummary']),
    ...mapState(useLaunchSetupStore, ['isSetupComplete']),
    ...mapState(usePreferenceStore, { preferencePanels: 'all' }),
  },

  methods: {
    async collection() {
      const collection = await this.fetchCollection();
      this.collections = groupBy(collection, 'collections');
    },

    async fetchCollection() {
      const collectionType = this.$route.meta.collection;
      let collection = null;

      switch (collectionType) {
        case 'preference':
          collection = this.getPreferenceCollection();
          this.title = 'app.preferences.title';
          break;
      }

      return this.filterCollection(collection);
    },

    getPreferenceCollection() {
      if (this.isSetupSummary) {
        const launchSetupStore = useLaunchSetupStore()
        return this.groupStoredChoices(launchSetupStore.summaryCollection)
      }

      return this.groupStoredChoices(addLocationCollection(
        this.preferencePanels,
        this.siteSummary,
        this.siteLocationSummary,
      ))
    },

    groupStoredChoices(collection) {
      if (!collection) return collection;

      const order = ['user_id', 'site', 'site_location', 'locations', 'geo_permission'];
      const rank = (item) => {
        const index = order.indexOf(item.name);
        return index === -1 ? order.length : index;
      };

      return [...collection]
        .map((item) => ({
          ...item,
          collections: item.collections === 'legal' ? 'legal' : 'user',
        }))
        .sort((first, second) => {
          const firstIsLegal = first.collections === 'legal';
          const secondIsLegal = second.collections === 'legal';
          if (firstIsLegal !== secondIsLegal) return firstIsLegal ? 1 : -1;
          return rank(first) - rank(second);
        });
    },

    filterCollection(collection) {
      return collection.filter(panel => {
        if (panel.name === 'summary') return false;
        if (this.getMode('testing')) return true;
        if (panel.collections === 'system') return false;
        return true;
      });
    },

    updateAllCopyPayloads() {
      if (this.isPreferencesSummary) return;

      setTimeout(() => {
        if (!this.$refs || Object.keys(this.$refs).length === 0) {
          return;
        }
        const builder = new SummaryPayloadBuilder(this, this.rtl);
        this.allCopyPayloads = builder.buildAllPayloads();
      }, 300);
    }
  },

  mounted() {
    this.collection();

    if (this.$route.meta.collection === 'preference') {
      resetEncounter();
    }
  },

  updated() {
    this.$nextTick(() => {
      if (this.$route.params.offset) {
        window.scroll(0, this.$route.params.offset);
      }
      this.updateAllCopyPayloads();
    });
  },

  watch: {
    '$route.name'() {
      this.collection();
    },

    language(newLang, oldLang) {
      if (newLang !== oldLang) {
        this.updateAllCopyPayloads();
      }
    },
  }
}
</script>
